import { useMemo } from 'react';
import { useGqlQuery } from '@API/useAvQuery';
import { FeatureFlags } from '@fabric-ui/components';
import { QueryKey, uniqBy } from '@fabric-ui/utils';
import { useMutation } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { useSnackbar } from 'notistack';
import { useAvContext } from '../../context/AvContextProvider';
import useHandleError from '../../hooks/useHandleError';
import { Permission, PermissionEntitiesNames } from '../../types';
import { getNetworkErrorCode } from '../../utils/utils';
import { FormattingCondition, FormattingRule } from './types';
import { NONE_ID } from './utils';

export const useFormattingRules = () => {
  const {
    userPermissions: { hasAllowedPermissionToResource },
  } = useAvContext();

  const hasPermissionsToReports = hasAllowedPermissionToResource({
    resource: PermissionEntitiesNames.REPORT,
    permission: Permission.READ,
  });
  return useGqlQuery<FormattingRule[]>({
    query: GET_FORMATTING_RULES,
    queryKey: [QueryKey.GETFORMATTINGRULES],
    enabled: hasPermissionsToReports,
  });
};

export interface EditCreateFormattingRule {
  formattingRule: FormattingRule;
  isCreate: boolean;
  onSuccess?: () => void;
}

export const cleanFormattingConditions = (formattingRule: Omit<FormattingRule, 'id'>) =>
  (formattingRule.formattingConditions as FormattingCondition[]).map(({ id, filter, ...rest }, index) => ({
    ...rest,
    ...(index === formattingRule.formattingConditions.length - 1 && formattingRule.fallbackLogic ? {} : { filter }),
  }));

export const useEditFormattingRule = () => {
  const { api, featureFlags } = useAvContext();
  const { enqueueSnackbar } = useSnackbar();
  const { refetch } = useFormattingRules();

  const handleError = useHandleError(featureFlags[FeatureFlags.ShowServerErrorInToast]);
  return useMutation({
    mutationFn: ({ formattingRule: f, isCreate, onSuccess }: EditCreateFormattingRule): Promise<any> => {
      const { id, ...formattingRule } = f;
      return api(isCreate ? CREATE_FORMATTING_RULE : UPDATE_FORMATTING_RULE, {
        options: {
          formattingRule: {
            ...formattingRule,
            ...(!isCreate && { id }),
            formattingConditions: cleanFormattingConditions(formattingRule),
          },
        },
        muteErrors: true,
        onSuccess: ({ data }) => {
          if (!data.errors) {
            enqueueSnackbar('Formatting Rule saved successfully', { variant: 'success' });
            refetch();
            onSuccess?.();
          }
          return true;
        },
      }).catch(e => {
        if (getNetworkErrorCode(e) === 400) {
          console.warn(e);
          enqueueSnackbar(e.message, { variant: 'error', style: { whiteSpace: 'pre-line' } });
        } else {
          handleError(e);
        }
      });
    },
  });
};

export const useDeleteFormattingRules = onSuccess => {
  const { enqueueSnackbar } = useSnackbar();
  const { api } = useAvContext();

  return useMutation({
    mutationFn: (ids: string[]) =>
      api(DELETE_FORMATTING_RULES, {
        options: { ids },
        onSuccess: () => {
          enqueueSnackbar(`Successfully Deleted Formatting Rules`, { variant: 'success' });
          onSuccess();
        },
      }),
  });
};

export const useFormattingRuleOptions = ({ additionalFormattingRule = [] }: { additionalFormattingRule?: FormattingRule[] }) => {
  const { data: formattingRules = [], isLoading: isLoadingFormattingRules } = useFormattingRules();
  const allFormattingRulesOptions = useMemo(
    () => uniqBy([{ name: 'None', id: NONE_ID }, ...additionalFormattingRule, ...formattingRules], v => v.id),
    [formattingRules, additionalFormattingRule]
  );

  return { formattingRules, isLoadingFormattingRules, allFormattingRulesOptions };
};

const GET_FORMATTING_RULES = gql`
  query findFormattingRules {
    findFormattingRules {
      id
      name
      fallbackLogic
      type
      fieldName
      formattingConditions {
        id
        color
        filter
      }
    }
  }
`;

const CREATE_FORMATTING_RULE = gql`
  mutation createFormattingRule($formattingRule: FormattingRuleDtoInput) {
    createFormattingRule(formattingRule: $formattingRule) {
      id
    }
  }
`;
const UPDATE_FORMATTING_RULE = gql`
  mutation updateFormattingRule($formattingRule: FormattingRuleDtoInput) {
    updateFormattingRule(formattingRule: $formattingRule) {
      id
    }
  }
`;

const DELETE_FORMATTING_RULES = gql`
  mutation deleteFormattingRules($ids: [String]) {
    deleteFormattingRules(ids: $ids)
  }
`;
